import { CommonDialogs, CommonTooltips, AuditLogView } from "@mcleod/common";
import {
    Label, Layout, EditRowDecorator, DecoratorType, DataSourceMode, ValidationResult, Table, Tab, TableRow, Button,
    Switch, TableRowBeforeSaveEvent, Panel, LookupModelSelectionEvent, Textbox, DataSource, TableRowDisplayEvent,
    TableRowMode, RowModeControlType, ClickEvent, Component, BlurEvent, ChangeEvent, LookupModelSearchEvent,
    DataSourceExecutionEvent, PrintableEvent, Snackbar, Image, HorizontalSpacer
} from "@mcleod/components";
import { ValidationUtil } from "@mcleod/components/src/databinding/ValidationUtil";
import { DecoratorAccessTester } from "@mcleod/components/src/page/decorators/DecoratorAccessTester";
import { LogManager, ModelRow, StringUtil, PermissionsUtil, ActionPermission, Api } from "@mcleod/core";
import { getDetentionControlBoolean, getDispatchControlBoolean } from "@mcleod/general";
import { ImageName } from "@mcleod/images";
import { overrideCommodityQuickInfoTooltip } from "@mcleod/powerbroker";
import { AppointmentUtil } from "./AppointmentUtil";
import { AutogenLayoutMultiStop } from "./autogen/AutogenLayoutMultiStop";
import { ContactUtil } from "./ContactUtil";
import { OrderTabView } from "./OrderTabView";
import { ServiceIncident } from "./ServiceIncident";
import { StopNotesAndReferenceNumbers } from "./StopNotesAndReferenceNumbers";
import { DetentionUtil } from "./DetentionUtil";


interface MultiStopSlideoutProps {
    isManifest: boolean;
    row: any,
    isDetention?: boolean,
    doAfterSlideOut?: (success: boolean) => void;
}

const log = LogManager.getLogger("lme.dispatch.multistop");

export class MultiStop extends AutogenLayoutMultiStop {
    private distanceBadgeArray: boolean[] = [];
    commentsAndRefNbrs: StopNotesAndReferenceNumbers;
    public isManifest: boolean;
    public isDetention: boolean;
    private labelOrderId: Label;
    private showDetention = getDetentionControlBoolean("is_active", false);

    static async showSlideout(props: MultiStopSlideoutProps): Promise<boolean> {
        if (!await Layout.canAccessLayout("lme/dispatch/MultiStop")) {
            CommonDialogs.showError("Access denied to lme/dispatch/MultiStop due to permissions");
            return false;
        }
        const layout = Layout.getLayout("lme/dispatch/MultiStop") as MultiStop;
        const isManifest = props.isManifest;
        const isDetention = props.isDetention ?? false;
        const isBrokered : boolean = props.row.get("brokerage") ?? false;
        const searchInput = isManifest ? { manifest_id: props.row.get("manifest_id"), movement_id: props.row.get("movement_id") } : { id: props.row.get("order_id") };
        new EditRowDecorator({
            layout: layout,
            title: isManifest ? `Stop Details - Manifest ${props.row.get("manifest_id")}` : `Stop Details - Order ${props.row.get("order_id")}`,
            fillVerticalSpace: true,
            layoutLoadListeners: async () => {
                layout.isDetention = isDetention;
                if (isManifest) {
                    layout.mainDataSource = layout.sourceManifestMultistop;
                    layout.sourceStop.parentDataSource = layout.sourceManifestMultistop;
                    layout.sourceStop.orderBy = [{ field: "movement_sequence", sort: "asc" }];
                    layout.isManifest = true;
                }

                if (DecoratorAccessTester.isDecoratorAllowed(layout, DecoratorType.UPDATE).displaysInViewMode) {
                    layout.displayInViewMode();
                }

                await layout.mainDataSource.search(searchInput);
                layout.mainDataSource.mode = DataSourceMode.UPDATE;
                layout.activeRow?.set("calc_target_pay", props.row.get("calc_target_pay"));
                layout.activeRow?.set("calc_max_pay", props.row.get("calc_max_pay"));
                const summaryData = layout.mainDataSource.summaryData;
                if (props.row.get("target_pay_method") === "S") {
                    layout.textboxTargetPay.captionVisible = false;
                    layout.panelMPact.visible = true;
                }
                layout.activeRow?.set("order_id", props.row.get("order_id"));
                overrideCommodityQuickInfoTooltip(layout.textboxCommodityId, layout.activeRow);
                if (isManifest) {
                    layout.panelOrderInfo.visible = false;
                    layout.panelManifestInfo.visible = true;
                    layout.panelManifestStopInfo.visible = isBrokered;
                }
                if (isManifest && summaryData?.get("customers").length === 1) {
                    layout.labelManifestCustAddress1.visible = true;
                    layout.citystateManifestCust.visible = true;
                }
            },
            width: '90%',
            overlayProps: { closeOnClickOff: true, greyedBackground: true },
            onSave: async () => {
                layout.postChanges();
            },
            onClose: (cancelled: boolean) => layout.slideOut(),
            doAfterSlideOut: () => {
                props.doAfterSlideOut(true);
            },
            onValidate: () => {
                const validationResults:ValidationResult[] = layout.validate(true, true);
                if (!ValidationUtil.checkValidationResults(validationResults)) {
                    ValidationUtil.showFailedValidationDialog(validationResults.filter(result => !result.isValid));
                }
                return validationResults;
            }
        });
        return true;
    }

    async postChanges() {
        const errorHandler = (error: any) => {}
        try {
            this.tableStop.allRows.forEach(row => {
                const tablePay = row.findComponentById("tablePay") as Table;
                if (tablePay && tablePay.dataSource != null) {
                    tablePay.allRows.forEach((tableRow) => {
                        const modelRow = tableRow.data;
                        modelRow.post();
                    });
                }
                const tableCharges = row.findComponentById("tableCharges") as Table;
                if (tableCharges && tableCharges.dataSource != null) {
                    tableCharges.allRows.forEach((tableRow) => {
                        const modelRow = tableRow.data;
                        modelRow.post();
                    });
                }
                const tabDetentionDetails = row.findComponentById("tabDetentionDetails") as Tab;
                if (tabDetentionDetails && tabDetentionDetails.dataSource != null) {
                    tabDetentionDetails.dataSource.post();
                }
        });
            await this.sourceMultiStop.post(errorHandler);
        } catch (e) {
             log.error("Error posting MultiStop changes:", e);
        }
    }

    static showSlideoutSimple(order_id: string, hideStopInfo: boolean = false, onSaveCallback?: () => void) {
        const multiStopLayout: MultiStop = Layout.getLayout("lme/dispatch/MultiStop") as MultiStop;
        new EditRowDecorator({
            title: `Stop Details - Order ${order_id}`,
            layout: multiStopLayout,
            layoutLoadListeners: async () => {
                await multiStopLayout.mainDataSource.search({ id: order_id });
                multiStopLayout.mainDataSource.mode = DataSourceMode.UPDATE;
                multiStopLayout.activeRow.set("order_id", order_id);
                if (hideStopInfo) {
                    multiStopLayout.panelStopInfo.visible = false;
                    multiStopLayout.panelWeight.borderTopRightRadius = 6;
                }
            },
            width: '80%',
            overlayProps: {closeOnClickOff: false, greyedBackground: true},
            onSave: async () => {
                await multiStopLayout.sourceMultiStop.post();
                if (onSaveCallback) {
                    onSaveCallback();
                }
            },
            onClose: (cancelled: boolean) => multiStopLayout.slideOut(),
            onValidate: () => {
                const validationResults:ValidationResult[] = multiStopLayout.validate(true, true);
                if (!ValidationUtil.checkValidationResults(validationResults)) {
                    ValidationUtil.showFailedValidationDialog(validationResults.filter(result => !result.isValid));
                }
                return validationResults;
            }
        });
    }

    setupTableRowListeners(tableRow: TableRow) {
        const btnExpandRow = tableRow.findComponentById("btnExpandRow") as Button;
        btnExpandRow?.addClickListener(event => this.btnExpandRowOnClick(event));
        const apptRequired = tableRow.findComponentById("switchApptRequired") as Switch;
        apptRequired?.addChangeListener(event => this.switchApptRequiredOnChange(event.target["row"]));
        const switchConfirmed = tableRow.findComponentById("switchConfirmed") as Switch;
        switchConfirmed?.addChangeListener(event => this.switchConfirmedOnChange(event));
    }

    /** This is an event handler for the beforeRowSave event of tableStop.  */
    tableStopBeforeRowSave(event: TableRowBeforeSaveEvent) {
        const tableRow: TableRow = event.target as TableRow;
        const changedData = tableRow.data.getChangedData();
        const stopNotesRefNumbersPanel = tableRow.findComponentById("panelStopCommentsReferenceNumbers") as Panel;
        const stopNotesRefNumbers: StopNotesAndReferenceNumbers = stopNotesRefNumbersPanel.components[0] as StopNotesAndReferenceNumbers;
        const modelRow: ModelRow = event.target["data"];
        modelRow.set("stop_notes", stopNotesRefNumbers.tableStopComments.data ?? []);
        modelRow.set("reference_numbers", stopNotesRefNumbers.tableStopReferenceNumbers.data ?? []);
        modelRow.set("bp_stop_slideout", true);

        if (AppointmentUtil.apptConfirmCheck(changedData, modelRow) == true && tableRow["appointmentChangeData"] != null) {
            tableRow["appointmentChangeData"].set("revised_early", changedData["sched_arrive_early"] ?? null);
            tableRow["appointmentChangeData"].set("revised_late", changedData["sched_arrive_late"] ?? null);
            tableRow["appointmentChangeData"].set("unconfirmed", changedData["confirmed"] != null && changedData["confirmed"] === "N");
            modelRow.set("appointment_change", tableRow["appointmentChangeData"]);
        }
    }

    textboxAuthStatusOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target);
        if (event.hasSelection()) {
            if (event.selection.get("update_billing_approved") != null || event.selection.get("update_detention_status") != null ||
                event.selection.get("update_payment_approval") != null) {
                    const panelManualNextActions = tableRow.findComponentById("panelManualNextActions") as Panel;
                    if (panelManualNextActions)
                        panelManualNextActions.visible = false;
                    const panelAutoNextActions = tableRow.findComponentById("panelAutoNextActions") as Panel;
                    if (panelAutoNextActions)
                        panelAutoNextActions.visible = true;
                    const switchAutoBilling = tableRow.findComponentById("switchAutoBilling") as Switch;
                    if (switchAutoBilling) {
                        switchAutoBilling.checked = event.selection.get("update_billing_approved") == "Y";
                    }
                    if (event.selection.get("payment_display")) {
                        const textboxtAutoPayment = tableRow.findComponentById("textboxtAutoPayment") as Textbox;
                        if (textboxtAutoPayment) {
                            textboxtAutoPayment.text = event.selection.get("payment_display");
                            textboxtAutoPayment.enabled = false;
                        }
                    }
                    const tabDetentionDetails = tableRow.findComponentById("tabDetentionDetails") as Tab;
                    if (tabDetentionDetails) {
                        const sourceDetentionHist = tabDetentionDetails.dataSource as DataSource;
                        sourceDetentionHist.activeRow.set("pay_approval", null);
                        sourceDetentionHist.activeRow.set("bill_approved", null);
                    }
                    return
                }
            }
            const panelManualNextActions = tableRow.findComponentById("panelManualNextActions") as Panel;
            if (panelManualNextActions)
                panelManualNextActions.visible = true;
            const panelAutoNextActions = tableRow.findComponentById("panelAutoNextActions") as Panel;
            if (panelAutoNextActions)
                panelAutoNextActions.visible = false;
    }

    /** This is an event handler for the onRowDisplay event of tableStop.  */
    tableStopOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow: TableRow = event.target as TableRow;
        const modelRow: ModelRow = tableRow.data as ModelRow;
        tableRow.hideDragHandle();
        tableRow.table.dataSource.preventChangeNotifications = true;
        tableRow.table.rows[tableRow.index].borderBottomWidth = 0;
        tableRow["appointmentChangeData"] = null;
        this.setupTableRowListeners(tableRow);
        this.switchApptRequiredOnChange(tableRow);

        const locationName = tableRow.findComponentById("textboxLocationName") as Textbox;
        const contactName: Textbox = tableRow.findComponentById("textboxContactName") as Textbox;
        const contactPhone: Textbox = tableRow.findComponentById("textboxPhone") as Textbox;
        const contactEmail: Textbox = tableRow.findComponentById("textboxContactEmail") as Textbox;
        const appointmentStatus: Textbox = tableRow.findComponentById("textboxAppointmentStatusId") as Textbox;
        const between: Textbox = tableRow.findComponentById("textboxStopDateType") as Textbox;
        const distanceBadge: Label = tableRow.findComponentById("labelDistanceBadge") as Label;
        const type: string = tableRow.data.get("stop_type");

        if (StringUtil.isEmptyString(modelRow.get("contact_name"))){
            contactName.text = "";
        }

        if (StringUtil.isEmptyString(modelRow.get("phone"))){
            contactPhone.text = "";
        }

        if (StringUtil.isEmptyString(modelRow.get("contact_email"))){
            contactEmail.text = "";
        }

        ContactUtil.configureTableRowContactLookup({
            nameTextbox: contactName,
            phoneTextboxId: "textboxPhone",
            emailTextboxId: "textboxContactEmail",
            contactNameField: "contact_name",
            contactPhoneField: "phone",
            contactEmailField: "contact_email"
        });

        if (StringUtil.isEmptyString(modelRow.get("appointment_status_id", "")))
            appointmentStatus.text = "";

        // if current stop or delivered - solid orange
        // all other badges clear
        const orderSequence = this.isManifest ? modelRow.get("movement_sequence") : modelRow.get("order_sequence");
        this.distanceBadgeArray[orderSequence] = (modelRow.get("actual_arrival") != null || modelRow.get("actual_departure") != null);

        if (orderSequence != 1) {
            if (this.distanceBadgeArray[orderSequence - 1] == true) {
                distanceBadge.backgroundColor = "warning";
                distanceBadge.color = "warning.reverse";
                distanceBadge.borderWidth = 0;
            }
            else {
                distanceBadge.backgroundColor = "warning.reverse";
                distanceBadge.color = "warning";
                distanceBadge.borderWidth = 1;
            }
        }

        if (locationName != null) {
            locationName.quickInfoOverrideKeyValue = modelRow.get("location_id");
        }

        if (modelRow.get("actual_departure") != null)
            between.visible = false;
        else if (modelRow.get("sched_arrive_late") == null)
            between.text = "On";
        else
            between.text = "Between";

        between.caption = type == "SO" ? "Delivery" : "Pickup";

        const early: Textbox = tableRow.findComponentById("textboxSchedArriveEarly") as Textbox;
        const late: Textbox = tableRow.findComponentById("textboxSchedArriveLate") as Textbox;
        const actualArrive: Textbox = tableRow.findComponentById("textboxActualArrival") as Textbox;
        const actualDepart: Textbox = tableRow.findComponentById("textboxActualDeparture") as Textbox;
        const orderIdLabel: Label = tableRow.findComponentById("labelOrderId") as Label;
        const buttonEditActual: Button = tableRow.findComponentById("buttonEditActual") as Button;
        this.labelOrderId = orderIdLabel;

        tableRow.mode = TableRowMode.UPDATE;
        tableRow.allowEdit = true;

        const hasActualArrival = !modelRow.isNull("actual_arrival");
        const hasActualDeparture = !modelRow.isNull("actual_departure");
        if (actualArrive != null)
            actualArrive.visible = actualArrive.required = hasActualArrival;
        if (actualDepart != null)
            actualDepart.visible = actualDepart.required = hasActualArrival;
        if (early != null)
            early.visible = !hasActualArrival;
        if (late != null)
            late.visible = !hasActualArrival && between.text == "Between";
        if (buttonEditActual != null) {
            buttonEditActual.visible = !this.isMovementNotEditable() && hasActualArrival && hasActualDeparture;
        }

        if (hasActualArrival && hasActualDeparture) {
            const panelDateComponents = tableRow.findComponentById("panelDateComponents") as Panel;
            panelDateComponents?.forEveryChildComponent(comp => {
                if (comp instanceof Textbox || comp instanceof Switch) {
                    comp.printable = comp.printableDuringUpdate = true;
                }
            });
        }

        const labelDistanceBadge: Label = tableRow.findComponentById("labelDistanceBadge") as Label;
        if (!this.isManifest && modelRow.get("rate_dist_from_previous") != null) {
            labelDistanceBadge.visible = true;
            labelDistanceBadge.text = modelRow.get("rate_dist_from_previous") + " MILES BETWEEN STOPS";
        } else if (this.isManifest && modelRow.get("move_dist_from_previous") != null) {
            labelDistanceBadge.visible = true;
            labelDistanceBadge.text = modelRow.get("move_dist_from_previous") + " MILES BETWEEN STOPS";
        }
        else {
            labelDistanceBadge.visible = false;
            labelDistanceBadge.text = "";
        }

        this.setAppointmentLabel(tableRow, modelRow, "labelApptImage", "confirmed", "appt_required");

        const panelStopCommentsReferenceNumbers = tableRow.findComponentById("panelStopCommentsReferenceNumbers") as Panel;
        const stopCommentRefNbr: StopNotesAndReferenceNumbers = panelStopCommentsReferenceNumbers.components[0] as StopNotesAndReferenceNumbers;
        if (stopCommentRefNbr) {
            stopCommentRefNbr.addLayoutLoadListener(event => {
                const stopRow = new ModelRow(this.sourceStop.url, true, modelRow.data);
                stopCommentRefNbr.stopRow = stopRow;
                this.setupCommentRefNbrTables(stopCommentRefNbr, hasActualArrival ? DataSourceMode.NONE : DataSourceMode.UPDATE);
            });
        }

        const serviceIncidentLayout = this.getServiceIncident(tableRow);
        if (serviceIncidentLayout) {
            serviceIncidentLayout.addLayoutLoadListener(() => {
                serviceIncidentLayout.visible = !modelRow.isNull("service_fail");
                if (serviceIncidentLayout.visible) {
                    this.setupServiceIncidents(serviceIncidentLayout, modelRow, true);
                }
            });
        }
        if (this.showDetention) {
            const detentionStatus = modelRow.get("detention_hist.status");
            const tabDetentionDetails = tableRow.findComponentById("tabDetentionDetails") as Tab;
            const totalPointsImage = new Image({ name: ImageName.detention, color: "error", rowBreak: false, height: 30, width: 30 });
            DetentionUtil.configureIconByDetentionStatus(detentionStatus, totalPointsImage);
            const text = modelRow.get("detention_hist.status") == "F" ? "Free Time Started"
                : modelRow.get("detention_hist.status") == "B" ? "Detention Charging" : modelRow.get("detention_hist.status") == "D" ? "Detention Occurred" :
                modelRow.get("detention_hist.status") == "C" ? "Detention Closed" : "";
            const totalPointsLabel = new Label({
                text: text,
                fontBold: true,
                color: "default",
                rowBreak: false,
            });
            const shouldSearchDetention = !StringUtil.isEmptyString(text);
            if (tabDetentionDetails) {
                if (shouldSearchDetention) {
                    tabDetentionDetails.heading.insert(totalPointsImage, tabDetentionDetails.heading.indexOf(tabDetentionDetails.headingLabel) + 2);
                    tabDetentionDetails.heading.insert(totalPointsLabel, tabDetentionDetails.heading.indexOf(totalPointsImage) + 1);
                    this.styleTab(tabDetentionDetails);
                    if (!StringUtil.isEmptyString(text)) {
                        tabDetentionDetails.addMountListener(async () => {
                            const textboxFreeTime = tableRow.findComponentById("textboxFreeTime") as Textbox;
                            const textboxTotalDetention = tableRow.findComponentById("textboxTotalDetention") as Textbox;
                            const textboxChangerFeeTimeStarts = tableRow.findComponentById("textboxChangerFeeTimeStarts") as Textbox;
                            const textboxChangerFeeTimeEnds = tableRow.findComponentById("textboxChangerFeeTimeEnds") as Textbox;
                            const textboxArrival = tableRow.findComponentById("textboxArrival") as Textbox;
                            const textbotWarning = tableRow.findComponentById("textbotWarning") as Textbox;
                            const textboxCharge = tableRow.findComponentById("textboxCharge") as Textbox;
                            const textboxDeparture = tableRow.findComponentById("textboxDeparture") as Textbox;
                            if (textboxFreeTime) {
                                textboxFreeTime.printable = true;
                            }
                            if (textboxTotalDetention) {
                                textboxTotalDetention.caption = modelRow.get("detention_hist.status") == "A" ||
                                modelRow.get("detention_hist.status") == "F" || modelRow.get("detention_hist.status") == "B"
                                 ? "Detention Accumulating"
                                    : "Total Detention";
                                textboxTotalDetention.printable = true;
                            }
                            if (textboxChangerFeeTimeStarts) {
                                textboxChangerFeeTimeStarts.printable = true;
                            }
                            if (textboxChangerFeeTimeEnds) {
                                textboxChangerFeeTimeEnds.printable = true;
                            }
                            if (textboxArrival) {
                                textboxArrival.printable = true;
                            }
                            if (textbotWarning) {
                                textbotWarning.printable = true;
                            }
                            if (textboxCharge) {
                                textboxCharge.printable = true;
                            }
                            if (textboxDeparture) {
                                textboxDeparture.printable = true;
                            }
                            if (shouldSearchDetention && (tabDetentionChargesPay.dataSource == null || tabDetentionChargesPay.dataSource.activeRow == null)) {
                                const sourceDetentionHist = new DataSource({ url: this.sourceDetentionHist.url });
                                sourceDetentionHist.layoutName = "lme/dispatch/DetentionHistory";
                                tabDetentionDetails.dataSource = sourceDetentionHist;
                                const buttonRecalculate = tableRow.findComponentById("buttonRecalculate") as Button;
                                if (buttonRecalculate) {
                                    buttonRecalculate.busy = true;
                                }
                                tabDetentionDetails.forEveryChildComponent(child => {
                                    child.dataSource = sourceDetentionHist;
                                });
                                await tabDetentionDetails.dataSource.search({ id: modelRow.get("detention_hist.id") });
                                if ("D" !== detentionStatus) {
                                    const textboxAuthNum = tableRow.findComponentById("textboxAuthNum") as Textbox;
                                    const textboxAuthStatus = tableRow.findComponentById("textboxAuthStatus") as Textbox;
                                    const textboxPaymentApproval = tableRow.findComponentById("textboxPaymentApproval") as Textbox;
                                    const switchBillingApproval = tableRow.findComponentById("switchBillingApproval") as Switch
                                    if ("C" === detentionStatus) {
                                        if (textboxAuthNum) {
                                            textboxAuthNum.printable = true;
                                        }
                                        if (textboxAuthStatus) {
                                            textboxAuthStatus.printable = true;
                                        }
                                        if (textboxPaymentApproval) {
                                            textboxPaymentApproval.printable = true;
                                        }
                                        if (switchBillingApproval) {
                                            switchBillingApproval.enabled = false;
                                        }
                                        tabDetentionChargesPay.forEveryChildComponent(child => {
                                            if (child instanceof Table) {
                                                const tableRows = child.allRows;
                                                tableRows.forEach(row => this.processTableRowByStatus(row, true));
                                            }
                                        })
                                    } else {
                                        [textboxAuthNum, textboxAuthStatus, textboxPaymentApproval, switchBillingApproval]
                                            .forEach((component) => {
                                                if (component) {
                                                    component.enabled = false;
                                                }
                                            });
                                    }
                                }
                                
                                if (buttonRecalculate) {
                                    buttonRecalculate.busy = false;
                                    buttonRecalculate.addClickListener(async () => {
                                        buttonRecalculate.busy = true;
                                        await sourceDetentionHist.search({ id: modelRow.get("detention_hist.id") });

                                        // Remove existing status elements
                                        const existingImage = tabDetentionDetails.heading.components.find(c => c instanceof Image);
                                        const existingLabel = tabDetentionDetails.heading.components.find(c => c instanceof Label && c !== tabDetentionDetails.headingLabel);
                                        if (existingImage) {
                                            tabDetentionDetails.heading.remove(existingImage);
                                        }
                                        if (existingLabel) {
                                            tabDetentionDetails.heading.remove(existingLabel);
                                        }

                                        // Get updated status and recreate elements
                                        const updatedStatus = sourceDetentionHist.activeRow?.get("status");
                                        const newImage = new Image({ name: ImageName.detention, color: "error", rowBreak: false, height: 30, width: 30 });
                                        DetentionUtil.configureIconByDetentionStatus(updatedStatus, newImage);
                                        const newText = updatedStatus == "F" ? "Free Time Started"
                                            : updatedStatus == "B" ? "Detention Charging"
                                            : updatedStatus == "D" ? "Detention Occurred"
                                            : updatedStatus == "C" ? "Detention Closed" : "";
                                        const newLabel = new Label({
                                            text: newText,
                                            fontBold: true,
                                            color: "default",
                                            rowBreak: false,
                                        });

                                        if ("C" === updatedStatus) {
                                            tabDetentionChargesPay.forEveryChildComponent(child => {
                                                if (child instanceof Table) {
                                                    const tableRows = child.allRows;
                                                    tableRows.forEach(row => this.processTableRowByStatus(row, true));
                                                }
                                            })
                                        }

                                        // Add new elements to tab heading
                                        if (!StringUtil.isEmptyString(newText)) {
                                            tabDetentionDetails.heading.insert(newImage, tabDetentionDetails.heading.indexOf(tabDetentionDetails.headingLabel) + 1);
                                            tabDetentionDetails.heading.insert(newLabel, tabDetentionDetails.heading.indexOf(newImage) + 1);
                                        }

                                        buttonRecalculate.busy = false;
                                    });
                                }
                                const textboxPaymentApproval = tableRow.findComponentById("textboxPaymentApproval") as Textbox;
                                if (textboxPaymentApproval) {
                                    textboxPaymentApproval.enabled = modelRow.get("pending_approval_enabled");
                                }
                            }
                        });
                        if (this.isDetention) {
                            tabDetentionDetails.expanded = true;
                        }
                    }
                }
                else {
                    tabDetentionDetails.visible = false;
                }
            }

            const tabDetentionChargesPay = tableRow.findComponentById("tabDetentionChargesPay") as Tab;
            if (tabDetentionChargesPay) {
                if (shouldSearchDetention) {
                this.styleTab(tabDetentionChargesPay);
                    tabDetentionChargesPay.addMountListener(async () => {
                        const tablePay = tableRow.findComponentById("tablePay") as Table;
                        const tableCharges = tableRow.findComponentById("tableCharges") as Table;
                        if (!shouldSearchDetention) {
                            // Remove rows in reverse order to avoid index shifting issues
                            for (let i = tableCharges?.allRows.length - 1; i >= 0; i--) {
                                tableCharges.removeRow(i);
                            }
                            for (let i = tablePay?.allRows.length - 1; i >= 0; i--) {
                                tablePay.removeRow(i);
                            }

                        }
                        if (tableCharges && shouldSearchDetention && (tableCharges.dataSource == null || tableCharges.dataSource.activeRow == null)) {
                            const sourceDetentionHistChg = new DataSource({ url: this.sourceDetentionHistChg.url });
                            tableCharges.dataSource = sourceDetentionHistChg;
                            await sourceDetentionHistChg.search({ detention_hist_id: modelRow.get("detention_hist.id") });
                            tableCharges.rowModeControlType = RowModeControlType.ALWAYS_EDIT;
                            sourceDetentionHistChg.displayDataInBoundComponents();
                        }
                        if (tablePay && shouldSearchDetention && (tablePay.dataSource == null || tablePay.dataSource.activeRow == null)) {
                            const sourceDetentionHistPay = new DataSource({ url: this.sourceDetentionHistPay.url });
                            tablePay.dataSource = sourceDetentionHistPay;
                            await tablePay.dataSource.search({ detention_hist_id: modelRow.get("detention_hist.id") });
                            tablePay.rowModeControlType = RowModeControlType.ALWAYS_EDIT;
                            sourceDetentionHistPay.displayDataInBoundComponents();
                        }
                        if ("D" !== detentionStatus) {
                            tabDetentionChargesPay.forEveryChildComponent(child => {
                                if (child instanceof Table) {
                                    const tableRows = child.allRows;
                                    tableRows.forEach(row => this.processTableRowByStatus(row, true));
                                }
                            })
                        }
                    });
                }
                else {
                    tabDetentionChargesPay.visible = false;
                }
            }
        } else {
            const tabDetentionDetails = tableRow.findComponentById("tabDetentionDetails") as Tab;
            if (tabDetentionDetails) {
                tabDetentionDetails.visible = false;
            }
            const tabDetentionChargesPay = tableRow.findComponentById("tabDetentionChargesPay") as Tab;
            if (tabDetentionChargesPay) {
                tabDetentionChargesPay.visible = false;
            }
        }
        if (this.isManifest === true) {
            orderIdLabel.visible = true;
            orderIdLabel.addUnderlineWhenMouseOver();
            const manifestModelRow = this.sourceManifestMultistop.data.filter((row) => row.data["orders.id"] === modelRow.get("order_id"));
            (tableRow.findComponentById("labelMovementSequence") as Label).visible = true;
            (tableRow.findComponentById("labelStopSequence") as Label).visible = false;
            CommonTooltips.setTooltipContentLayout(orderIdLabel, manifestModelRow[0]?.data["orders.customer_id"], "lme/ar/CustomerQuickInfo");
        }

        const buttonAudit = tableRow.findComponentById("buttonAudit") as Button;
        if (buttonAudit) {
            buttonAudit.addClickListener(event => AuditLogView.showAuditForRow("lme/dispatch/MultiStop", this.sourceStop, modelRow));
            if (buttonAudit.imageProps) {
                buttonAudit.imageProps.tooltip = "Audit log";
            }
            buttonAudit.borderWidth=0;
        }

    }

    textboxChargeOnBlur(event) {
        const tableRow = event.target.row as TableRow;
        const units = tableRow.data.get("units");
        const rate = tableRow.data.get("rate");
        const chargeAmount = units * rate;
        if (!isNaN(chargeAmount)) {
            tableRow.data.set("charge_amount", chargeAmount);
        } else {
            tableRow.data.set("charge_amount", null);
        }
        this.resolveManualEdit(tableRow, "imageChgManualEdit");
    }

    textboxPayOnBlur(event) {
        const tableRow = event.target.row as TableRow;
        const units = tableRow.data.get("units");
        const rate = tableRow.data.get("rate");
        const chargeAmount = units * rate;
        if (!isNaN(chargeAmount)) {
            tableRow.data.set("pay_amount", chargeAmount);
        } else {
            tableRow.data.set("pay_amount", null);
        }
        this.resolveManualEdit(tableRow, "imagePayManualEdit");
    }

     resolveManualEdit(tableRow: TableRow, iconId: string) {
        const modelRow = tableRow.data as ModelRow;
        if (modelRow.hasChanged() && !modelRow.originalData?.manual_edit) {
            const changes = modelRow.getChangedData();
            const manualEdit = ("units" in changes || "rate" in changes);
            modelRow.set("manual_edit", manualEdit);
            const icon = tableRow.findComponentById(iconId);
            if (icon) {
                icon.visible = manualEdit;
            }
        }
    }

    tableChargesOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow: TableRow = event.target as TableRow;
        const isNoEditStatus = this.processTableRowByStatus(tableRow);
        if (!isNoEditStatus) {
            const textboxUnits: Textbox = tableRow.findComponentById("textboxUnits") as Textbox;
            const textboxRate: Textbox = tableRow.findComponentById("textboxRate") as Textbox;
            if (textboxUnits != null) {
                textboxUnits.enabled = true;
            }
            if (textboxRate != null) {
                textboxRate.enabled = true;
            }
        }
        const modelRow = tableRow.data as ModelRow;
        const manualEdit = modelRow.getBoolean("manual_edit", false);
        const imageChgManualEdit = tableRow.findComponentById("imageChgManualEdit");
        if (imageChgManualEdit) {
            imageChgManualEdit.visible = manualEdit;
        }
    }

    tablePayOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow: TableRow = event.target as TableRow;
        const isNoEditStatus = this.processTableRowByStatus(tableRow);
        if (!isNoEditStatus) {
            const textboxUnitsPay: Textbox = tableRow.findComponentById("textboxUnitsPay") as Textbox;
            const textboxRatePay: Textbox = tableRow.findComponentById("textboxRatePay") as Textbox;
            if (textboxUnitsPay != null) {
                textboxUnitsPay.enabled = true;
            }
            if (textboxRatePay != null) {
                textboxRatePay.enabled = true;
            }
        }
        const modelRow = tableRow.data as ModelRow;
        const manualEdit = modelRow.getBoolean("manual_edit", false);
        const imagePayManualEdit = tableRow.findComponentById("imagePayManualEdit");
        if (imagePayManualEdit) {
            imagePayManualEdit.visible = manualEdit;
        }
    }

    processTableRowByStatus(tableRow: TableRow, override: boolean = false) {
        const modelRow: ModelRow = tableRow.data as ModelRow;
        const status = modelRow.get("status", null);
        const isNoEditStatus = override || ["C", "V"].includes(status);
        if (isNoEditStatus) {
            tableRow.forEveryChildComponent(comp => {
                if (comp instanceof Textbox) {
                    comp.printable = true;
                }
            });
        }

        return isNoEditStatus;
    }

    styleTab(tab: Tab) {
        if (tab.heading != null && tab.headingLabel != null) {
            tab.heading.color = "subtle.light";
            tab.headingLabel.setProps({ color: "default", fontBold: true });
        }
    }

    /** This is an event handler for the onClick event of btnExpandRow.  */
    btnExpandRowOnClick(event: ClickEvent) {
        const tableRow: TableRow = TableRow.getContainingTableRow(event.target as Component);
        const row = tableRow.data;
        const expandablePanel: Component = tableRow.findComponentById("expandablePanel");
        tableRow["custom_expanded"] = !(!!tableRow["custom_expanded"]);
        event.target["imageRotation"] = tableRow["custom_expanded"] ? 0 : 90;
        expandablePanel.visible = !!tableRow["custom_expanded"];

        if (tableRow["custom_expanded"]) {
            // Populate the location website
            if (row.data.location != null && row.data.location[0].website_url != null) {
                let url = row.data.location[0].website_url;
                if (!url.startsWith("http:") && !url.startsWith("https")) {
                    url = "http://" + url;
                }
                row.set("location_website", url);
            }
        }
    }

    /** This is an event handler for the onChange event of textboxStopDateType.  */
    textboxStopDateTypeOnChange(event) {
        const tableRow: TableRow = TableRow.getContainingTableRow(event.target as Component);
        const type: string = tableRow.data.get("stop_type");
        const early: Textbox = tableRow.findComponentById("textboxSchedArriveEarly") as Textbox;
        const late: Textbox = tableRow.findComponentById("textboxSchedArriveLate") as Textbox;
        const between = event.newValue == "Between";
        late.required = false;
        late.visible = between;
        if (between) {
            late["_captionLabel"].preventCollapse = true;
            late["_captionLabel"].visible = false;
            late.required = true;
            late.placeholder = "Required";
        }
        else {
            tableRow.data?.set("sched_arrive_late", null);
        }
        this.setStopDateCaptions(early as Textbox, type == "SO", between);
    }

    setStopDateCaptions(early: Textbox, isDelivery: boolean, isDateWindow: boolean) {
        const caption = isDelivery ? "Delivery" : "Pickup";
        early.caption = isDateWindow ? `${caption} Window` : `${caption} Appointment`;
    }

    scheduleArriveOnBlur(event: BlurEvent) {
        AppointmentUtil.checkAppointment(event);
    }

    /** This is an event handler for the onChange event of switchApptRequired.  */
    switchApptRequiredOnChange(tableRow: TableRow) {
        const switchConfirmed = tableRow.findComponentById("switchConfirmed") as Switch;
        const apptRequired = tableRow.findComponentById("switchApptRequired") as Switch;
        switchConfirmed.visible = getDispatchControlBoolean("appt_confirmation", false) && apptRequired.checked;
        apptRequired.enabled = !tableRow.data.getBoolean("confirmed");
    }

    /** This is an event handler for the onChange event of switchConfirmed.  */
    switchConfirmedOnChange(event: ChangeEvent) {
        const tableRow: TableRow = TableRow.getContainingTableRow(event.target as Component);
        const apptRequired = tableRow.findComponentById("switchApptRequired") as Switch;
        const modelRow = tableRow.data;
        const changedData = modelRow.getChangedData();
        if (event.userInitiatedChange &&
            changedData["confirmed"] != null && changedData["confirmed"] === "N") {
            AppointmentUtil.showAppointmentChangeDialog({ "confirmed": "Y" }, null, modelRow, { switchConfirmed: event.target as Switch }, tableRow);
        } else {
            apptRequired.enabled = !modelRow.getBoolean("confirmed");
        }
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxContactName.  */
    textboxContactNameBeforeLookupModelSearch(event:LookupModelSearchEvent) {
        const locationId = TableRow.getContainingTableRow(event.target).data.get("location_id");
        if (locationId) {
            event.filter.parent_row_type = "L";
            event.filter.parent_row_id = locationId;
            event.filter.is_active = "Y";
        } else {
            event.preventDefault();
        }
    }

    setupCommentRefNbrTables(stopCommentRefNbr: StopNotesAndReferenceNumbers, mode: DataSourceMode) {
        const tableStopComment = stopCommentRefNbr.tableStopComments;
        const tableStopRefNbr = stopCommentRefNbr.tableStopReferenceNumbers;
        tableStopComment.dataSource.parentDataSource = tableStopRefNbr.dataSource.parentDataSource = this.sourceMultiStop;
        tableStopComment.dataSource.mode = tableStopRefNbr.dataSource.mode = mode;
    }

    private setAppointmentLabel(tableRow: TableRow, modelRow: any, labelId: string, confirmedField: string, requiredField: string) {
        const panelAppt = tableRow.findComponentById("panelAppt") as Panel;
        const labelAppt = tableRow.findComponentById(labelId) as Label;
        labelAppt.imageMarginLeft = 0;
        const apptRequired = modelRow.getBoolean(requiredField);
        if (apptRequired) {
            if (modelRow.getBoolean(confirmedField)) {
                labelAppt.text = "Confirmed";
                labelAppt.imageColor = "green";
                labelAppt.imageName = ImageName.appointmentConfirmed;
            } else {
                labelAppt.text = "Required";
                labelAppt.imageColor = "orange";
                labelAppt.imageName = ImageName.appointmentRequired;
            }
        }
        panelAppt.visible = apptRequired;
    }

    /** This is an event handler for the afterExecution event of sourceManifestMultistop. */
    sourceManifestMultistopAfterExecution(event: DataSourceExecutionEvent) {
        this.sourceManifestMultistop.activeRow?.set("total_weight", this.sourceManifestMultistop.summaryData.get("total_weight"));
    }

    orderNumberOnClick(event: ClickEvent) {
        OrderTabView.navigateTo({ key: (event.target as Label).text, newTab: true });
    }

    textboxAppointmentStatusIdOnPrintable(event: PrintableEvent) {
        const textboxAppointmentStatusId = event.target as Textbox;
        textboxAppointmentStatusId.visible = !textboxAppointmentStatusId.printable || !StringUtil.isEmptyString(textboxAppointmentStatusId.text);
    }

    buttonEditActualOnClick(event: ClickEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target as Button);
        const modelRow = tableRow.data as ModelRow;
        const actualArrive: Textbox = tableRow.findComponentById("textboxActualArrival") as Textbox;
        const actualDepart: Textbox = tableRow.findComponentById("textboxActualDeparture") as Textbox;
        const buttonEditActual: Button = event.target as Button;
        const printable = actualArrive.printable;
        if (!printable) {
            modelRow.revertToOriginalData(["actual_arrival", "actual_departure"]);
            if (modelRow.isNull("service_fail")) {
                const serviceIncidentLayout = this.getServiceIncident(tableRow);
                if (serviceIncidentLayout) {
                    serviceIncidentLayout.visible = false;
                    serviceIncidentLayout.sourceServicefail.parentDataSource = null;
                    serviceIncidentLayout.sourceServicefail.clear();
                }
            }
        }

        actualArrive?.setProps({ printable: !printable, printableDuringUpdate: !printable });
        actualDepart?.setProps({ printable: !printable, printableDuringUpdate: !printable });
        buttonEditActual.imageName = printable ? ImageName.x : ImageName.pencil;
        buttonEditActual.tooltip = printable ? "Cancel" : "Update actual arrival/departure time";
    }

    isMovementNotEditable(): boolean {
        const isUpdateMovementsAfterDeliveredDenied = PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_UPDATE_DEL_MOVES);
        const isUpdateMovementsAfterSettledDenied = PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_UPDATE_MOVE_AFTER_SETTLED);
        const isDelivered = this.activeRow?.get("movement.status", null) === "D";
        const isTransferred: boolean = !this.activeRow.isNull("movement.xfer2settle_date");
        const isBilled: boolean = !this.activeRow.isNull("bill_date");
        return (isUpdateMovementsAfterDeliveredDenied && isDelivered) || (isUpdateMovementsAfterSettledDenied && (isTransferred || isBilled));
    }

    async validateDateOnBlur(event: BlurEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target as Textbox);
        const modelRow = tableRow.data as ModelRow;
        const serviceIncidentLayout = this.getServiceIncident(tableRow);
        if (event.changedWhileFocused && serviceIncidentLayout) {
            await this.validateDate(modelRow, serviceIncidentLayout);
        }
    }

    async validateDate(stopData: ModelRow, serviceIncidentLayout: ServiceIncident) {
        try {
            const res = await Api.post("lme/dispatch/validate-stop", {
                stop_id: stopData.get("id"),
                stop_data: stopData,
                ignore_actuals_validation: true
            });
            if (stopData.isNull("service_fail") && res != null) {
                serviceIncidentLayout.visible = res.data[0].has_service_failure != null;
                if (serviceIncidentLayout.visible) {
                    this.setupServiceIncidents(serviceIncidentLayout, stopData);
                }
            }
            return true;
        } catch (e) {
            Snackbar.showWarningSnackbar(e.messages[0]);
            return false;
        }
    }

    getServiceIncident(tableRow: TableRow): ServiceIncident {
        const serviceIncidents = tableRow.findComponentsByType(ServiceIncident) as ServiceIncident[];
        if (serviceIncidents && serviceIncidents.length > 0) {
            return serviceIncidents[0];
        }
        return null;
    }

    setupServiceIncidents(serviceIncidentLayout: ServiceIncident, stopRow: ModelRow, sfExists: boolean = false) {
        const defaultValues = { fault_of_carrier_or_driver: true, edi_code_version: "004010", edi_element_id: "1651", stop_id: stopRow.get("id"), is_reportable: true,
            caused_by_type: "D", order_id: this.activeRow?.get("order_id"), stop_no: stopRow.get("order_sequence"), movement_id: stopRow.get("movement_id")
        };
        const dataSourceMode = sfExists ? DataSourceMode.UPDATE : DataSourceMode.ADD;
        if (!serviceIncidentLayout.sourceServicefail.activeRow) {
            const sfRow = new ModelRow("lme/dispatch/servicefail", !sfExists, sfExists ? stopRow.get("service_fail") : defaultValues);
            serviceIncidentLayout.orderCustomerId = this.activeRow?.get("customer_id");
            serviceIncidentLayout.sourceServicefail.setRowsAndMode(dataSourceMode, [sfRow], null);
            serviceIncidentLayout.sourceServicefail.parentDataSource = this.sourceStop.parentDataSource;
        }
    }

    /** This is an event handler for the onClick event of buttonAudit. */
    buttonAuditOnClick(event: ClickEvent) {
        log.info("Audit button clicked but action has not been set for a table row");
    }
}
